import React from "react";
import "../../index.css";
import { Link } from "react-router";
import { Tooltip } from "react-tooltip";
import platte from '../../Assets/Img/platte.jpg'

const Chap4 = () => {
  return (
    <div className="chapter-container">

      <div className="chapterFooter">
        <Link to="/Chap3">Previous</Link>
        <Link to="/">Home</Link>
        <Link to="/Chap5">Next</Link>
      </div>

      <h1 className="chapterHeading">
        Chapter 4: Source of Joseph’s Ideas on Zion
      </h1>

      <img src={platte} className='image' alt='pProposed platte of the Mormon city of Zion in Jackson County' />

    <div className="wrapper">

      <div className="paragraph">
        <p>
          The term “Zion” appears in the Old Testament in 2 Samuel 5:7. This
          verse refers to a hill, Mount Zion, located near Jerusalem. Mount Zion
          was a Jebusite fortress conquered by King David and renamed the City
          of David. Throughout Jewish history, the term Zion has come to
          simultaneously refer to the Temple Mount, the city of Jerusalem, and
          all the land of Israel. Zion also holds symbolic meaning for the Jews.
          Zion is the spiritual center of Israel. It also represents “The World
          to Come” with Zion as the location of the heavenly paradise of the
          afterlife.
        </p>
      </div>

      <div className="paragraph">
        <p>
          Zion is mentioned 152 times in the Bible, most often in Psalms,
          Isaiah, Jeremiah, and Lamentations. In Psalm 137, Zion is remembered
          from the perspective of the Babylonian captivity, after the
          Babylonians sacked Jerusalem in 586 BCE. “By the rivers of Babylon,
          there we sat down, yea, we wept, when we remembered Zion. We hanged
          our harps upon the willows in the midst thereof. For there they that
          carried us away captive required of us a song; and they that wasted us
          required of us mirth, saying, Sing us one of the songs of Zion.” 
          <btn id="clickable1" className="footnote">1  </btn>
          <Tooltip anchorSelect="#clickable1" className="footnote-size"  clickable>D&C 105:2-4.</Tooltip>
          
          Psalm 147 addresses the faithful. “The Lord doth build up Jerusalem: he
          gathereth together the outcast of Israel.” 
          <btn id="clickable2" className="footnote">2  </btn>
          <Tooltip anchorSelect="#clickable2" className="footnote-size"  clickable>Psalm 147:2.</Tooltip>
          
          “Praise the Lord, O
          Jerusalem; praise thy God, O Zion.” 
          <btn id="clickable3" className="footnote">3  </btn>
          <Tooltip anchorSelect="#clickable3" className="footnote-size"  clickable>Psalm 147:12.</Tooltip>
          
          Babylonian captivity lasted around
          60 years, after which groups of Jews returned to Jerusalem, their
          Zion.
        </p>
      </div>

      <div className="paragraph">
        <p>
          Zion also has theological meanings. In Psalms, Zion is mentioned as a
          place of God’s presence and glory.
          <btn id="clickable4" className="footnote">4  </btn>
          <Tooltip anchorSelect="#clickable4" className="footnote-size"  clickable>Psalm 2:6, 9:11, 14:7.</Tooltip>
          
           Isaiah refers to Zion to symbolize
          the suffering and redemption of Jews and Jerusalem. 
          <btn id="clickable5" className="footnote">5  </btn>
          <Tooltip anchorSelect="#clickable5" className="footnote-size"  clickable>Isaiah 1:27, 2:3, 52:1-2.</Tooltip>
          
          Jeremiah depicted
          Zion as a place of lament but also of future hope. 
          <btn id="clickable6" className="footnote">6  </btn>
          <Tooltip anchorSelect="#clickable6" className="footnote-size"  clickable>Jeremiah 3:14, 31:6.</Tooltip>
          
          Psalm 48 is a song
          of Zion. It refers to Jerusalem as the city of God, the dwelling place
          of God and a source of inspiration for the world. “Beautiful in its
          loftiness, the joy of the whole earth, like the heights of Zaphon is
          Mount Zion, the city of the Great King.” 
          <btn id="clickable7" className="footnote">7  </btn>
          <Tooltip anchorSelect="#clickable7" className="footnote-size"  clickable>Psalm 48:2.</Tooltip>
          
          “The heights of Zaphon”
          refers to a mythical mountain that symbolized divine presence.
        </p>
      </div>

      <div className="paragraph">
        <p>
          Mount Zion is also a place of divine rule when God’s kingdom is fully
          realized. “For the Lord has chosen Zion, he has desired it for his
          dwelling, saying, ‘This is my resting place for ever and ever; here I
          will sit enthroned, for I have desired it.’” 
          <btn id="clickable8" className="footnote">8  </btn>
          <Tooltip anchorSelect="#clickable8" className="footnote-size"  clickable>Psalm 132:13-14.</Tooltip>
          
          “Here am I, and the
          children the Lord has given me. We are signs and symbols in Israel
          from the Lord Almighty, who dwells on Mount Zion.” 
          <btn id="clickable9" className="footnote">9  </btn>
          <Tooltip anchorSelect="#clickable9" className="footnote-size"  clickable>Isaiah 8:18.</Tooltip>
          
          “The law will go
          out from Zion, the word of the Lord from Jerusalem.” 
          <btn id="clickable10" className="footnote">10  </btn>
          <Tooltip anchorSelect="#clickable10" className="footnote-size"  clickable>Isaiah 2:2-3.</Tooltip>
          
          “The Lord will
          rule over them in Mount Zion from that day and forever.”
          <btn id="clickable11" className="footnote">11  </btn>
          <Tooltip anchorSelect="#clickable11" className="footnote-size"  clickable>Micah 4:7</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          As we will see, these Old Testament scriptures about Zion had a
          profound impact on Joseph Smith and the early Mormons.
        </p>
      </div>

      <h2 className="headlinePadding" >Origin of Joseph’s Idea of Zion, the New Jerusalem</h2>

      <div className="paragraph">
        <p>
          Where did Joseph get the idea of building a holy city of Zion, a New
          Jerusalem, in the Americas? The Book of Revelation states, “And I John
          saw the Holy city, New Jerusalem, coming down from God out of heaven,
          prepared as a bride adorned for her husband. And I heard a great voice
          out of heaven saying, Behold, the tabernacle of God is with men, and
          he will dwell with them.” 
          <btn id="clickable12" className="footnote">12  </btn>
          <Tooltip anchorSelect="#clickable12" className="footnote-size"  clickable>Revelation 21:2-3.</Tooltip>
          
          This sounds much like the Book of Mormon’s
          description of Christ in the Americas. “And behold, this people
          (future Lamanites) will I establish in this land (America), unto the
          fulfilling of the covenant which I made with your father Jacob; and it
          shall be a New Jerusalem. And the powers of heaven shall be in the
          midst of this people; yea, even I will be in the midst of you.”
          <btn id="clickable13" className="footnote">13  </btn>
          <Tooltip anchorSelect="#clickable13" className="footnote-size"  clickable>3 Nephi 20:21–22.</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          More than two centuries prior to Joseph Smith’s day, America was a
          destination for new religious freedom. The Pilgrims arrived at
          Plymouth Rock in 1620, followed by the Puritans in 1630. Other
          religious groups followed. The Puritans believed they were a chosen
          people of God, living in the promised land of America that they
          sometimes called the new Israel. It has been stated, “The Puritans saw
          themselves as a chosen people, commissioned by God to build a New
          Jerusalem, or a City of Zion, in America – an exemplary community that
          all could observe and emulate.”
          <btn id="clickable14" className="footnote">14  </btn>
          <Tooltip anchorSelect="#clickable14" className="footnote-size"  clickable>James B. Allen and Glen M. Leonard, The Story of the Latter-day Saints, 1976, p. 9.</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          The idea of a New Jerusalem in America was referenced in the 17th
          century by Samuel Sewall when he asked, “… why the Heart of America
          may not be the seat of the New Jerusalem?” Puritan clergyman Cotton
          Mather stated that the New Jerusalem would be westward, beyond the
          confines of New England. 
          <btn id="clickable15" className="footnote">15  </btn>
          <Tooltip anchorSelect="#clickable15" className="footnote-size"  clickable>Alan Heimert, “Puritanism, the Wilderness, and the Frontier,” The New England Quarterly 26 (September 1953) 380-81. See also Gustav H. Blanke with Karen Lynn, “God’s Base of Operations: Mormon Variations on the American Sense of Mission,” Brigham Young University Studies 20 (Fall 1979):83-92.</Tooltip>
          
          While some looked for a New Jerusalem in the
          state of New York, others spiritualized the idea and spoke of Zion in
          the revivals of the 1820s. Such expressions as “growing zeal for the
          prosperity of Zion,” and “enquiring the way to Zion” and “wishes well
          to the cause of Zion” were expressions of the revival movement and
          referred to the building up of churches.
          <btn id="clickable16" className="footnote">16  </btn>
          <Tooltip anchorSelect="#clickable16" className="footnote-size"  clickable>Rev. George Lane, “Revival of Religion on Ontario District,” The Methodist Magazine (New York) 8 (April 1825):161, letter dated 25 January 1825; The Christian Herald (Portsmouth) 8 (March 1825):7 and Wayne Sentinel (Palmyra, NY) 2 (2 March 1825):4. 
            The minutes of the Geneva Presbytery states: “In the congregation of Palmyra [NY], the Lord has appeared in his glory to build up Zion.” (Geneva Presbytery Minutes, Presbyterian Historical Society, Philadelphia, D:27. For an extract of this record see Dialogue: A Journal of Mormon Thought 4 (Spring 1969):65.</Tooltip>
        </p>
      </div>

      <h2 className="headlinePadding" >God’s Directive to the Mormons</h2>

      <div className="paragraph">
        <p>
          Joseph convinced the early Mormons that it was God’s directive to
          them, as descendants of the Tribes of Israel and exclusive holders of
          God’s restored priesthood authority, to establish Zion as a center for
          God’s power in Jackson County, Missouri. Notice the Old Testament
          references in these letters Joseph wrote about establishing Zion.
        </p>
      </div>

      <div className="paragraph">
        <p>
          Joseph wrote in 1833, “The city of Zion spoken of by David, in the one
          hundred and second Psalm, will be built upon the land of America, ‘And
          the ransomed of the Lord shall return, and come to Zion with songs and
          everlasting joy upon their heads’ (Isaiah 35:10); and then they will
          be delivered from the overflowing scourge that shall pass through the
          land. But Judah shall obtain deliverance at Jerusalem. (Joel 2:32;
          Isaiah 26:20–21; Jeremiah 31:12; Psalm 1:5; Ezekiel 34:11–13.) These
          are testimonies that the Good Shepherd will put forth [to] His own
          sheep and lead them out from all nations where they have been
          scattered in a cloudy and dark day, to Zion, and to Jerusalem.”
          <btn id="clickable17" className="footnote">17  </btn>
          <Tooltip anchorSelect="#clickable17" className="footnote-size"  clickable>History of the Church, 1:315; from a letter from Joseph Smith to N.C. Saxton, Jan. 4,1833.</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          In another letter Joseph wrote in 1835, “I shall begin by quoting from
          the prophecy of Enoch, speaking of the last days: ‘Righteousness and
          truth will I cause to sweep the earth as with a flood, to gather out
          mine own elect from the four quarters of the earth, unto a place which
          I shall prepare, a holy city, that my people may gird up their loins,
          and be looking forth for the time of my coming, for there shall be my
          tabernacle, and it shall be called Zion, a New Jerusalem (Moses 7:62).
        </p>
      </div>

      <div className="paragraph">
        <p>
          “Now I understand by this quotation, that … righteousness and truth
          are to sweep the earth as with a flood. And now, I ask, how
          righteousness and truth are going to sweep the earth as with a flood?
          I will answer. Men and angels are to be co-workers in bringing to pass
          this great work, and Zion is to be prepared, even a new Jerusalem, for
          the elect that are to be gathered from the four quarters of the earth,
          and to be established an holy city, for the tabernacle of the Lord
          shall be with them …
        </p>
      </div>

      <div className="paragraph">
        <p>
          “… ’Behold this people will I establish in this land, unto the
          fulfilling of the covenant which I made with your father Jacob, and it
          shall be a New Jerusalem.’ (3 Nephi 20:22.) Now we learn from the Book
          of Mormon the very identical continent and spot of land upon which the
          New Jerusalem is to stand, and it must be caught up according to the
          vision of John upon the isle of Patmos.
        </p>
      </div>

      <div className="paragraph">
        <p>
          “Now many will feel disposed to say, that this New Jerusalem spoken
          of, is the Jerusalem that was built by the Jews on the eastern
          continent. But you will see, from Revelation 21:2, there was a New
          Jerusalem coming down from God out of heaven, adorned as a bride for
          her husband; that after this, the Revelator was caught away in the
          Spirit, to a great and high mountain, and saw the great and holy city
          descending out of heaven from God. Now there are two cities spoken of
          here. As everything cannot be had in so narrow a compass as a letter,
          I shall say with brevity, that there is a New Jerusalem to be
          established on this continent, and also Jerusalem shall be rebuilt on
          the eastern continent (see Ether 13:1–12). ‘Behold, Ether saw the days
          of Christ, and he spake also concerning the house of Israel, and the
          Jerusalem from whence Lehi should come; after it should be destroyed,
          it should be built up again, a holy city unto the Lord, wherefore it
          could not be a New Jerusalem, for it had been in a time of old.’
          (Ether 13:4–5.)”
          <btn id="clickable18" className="footnote">18  </btn>
          <Tooltip anchorSelect="#clickable18" className="footnote-size"  clickable>History of the Church, 2:260-2, letter from Joseph Smith to Elders of the Church Nov. 1835; Published in the Messenger & advocate Nov. 1835, p. 209-210.</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          Joseph also wrote, “The Prophets have said concerning Zion in the last
          days: how the glory of Lebanon is to come upon her; the fir tree, the
          pine tree, and the box tree together, to beautify the place of His
          sanctuary, that He may make the place of His feet glorious (see Isaiah
          60:13). Where for brass, He will bring gold; and for iron, He will
          bring silver; and for wood, brass; and for stones, iron (see Isaiah
          60:17); and where the feast of fat things will be given to the just
          (see Isaiah 25:6); yea, when the splendor of the Lord is brought to
          our consideration for the good of His people, the calculations of men
          and the vain glory of the world vanish, and we exclaim, ‘Out of Zion
          the perfection of beauty, God hath shined.’ (Psalm 50:2.)”
          <btn id="clickable19" className="footnote">19  </btn>
          <Tooltip anchorSelect="#clickable19" className="footnote-size"  clickable>History of the Church, 1:198, Book A1, p. 139.</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          Joseph put his ideas more succinctly in his Tenth Article of faith,
          “We believe in the literal gathering of Israel and in the restoration
          of the Ten Tribes; that Zion (the New Jerusalem) will be built upon
          the American continent.” 
          <btn id="clickable20" className="footnote">20  </btn>
          <Tooltip anchorSelect="#clickable20" className="footnote-size"  clickable>Articles of Faith 1:10.</Tooltip>
          
          Joseph revealed this about the future Zion,
          “Ye are come unto Mount Zion, and unto the city of the living God, the
          heavenly Jerusalem, and to an innumerable company of angels, to the
          general assembly and Church of the Firstborn.” 
          <btn id="clickable21" className="footnote">21  </btn>
          <Tooltip anchorSelect="#clickable21" className="footnote-size"  clickable>Joseph Smith, Teaching of the Prophet Joseph Smith, p. 320.</Tooltip>
          
          Joseph revealed of the
          holy city, “a cloud shall rest upon it, which cloud shall be even the
          glory of the Lord, which shall fill the house.”
          <btn id="clickable22" className="footnote">22  </btn>
          <Tooltip anchorSelect="#clickable22" className="footnote-size"  clickable>D&C 97:19.</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          In the Book of Mormon Nephi quotes Isaiah teaching about the future
          Zion, “And the Lord will create upon every dwelling place of mount
          Zion, and upon her assemblies, a cloud and smoke by day and the
          shining of a flaming fire by night; for upon all the glory of Zion
          shall be a defence.” 
          <btn id="clickable23" className="footnote">23  </btn>
          <Tooltip anchorSelect="#clickable23" className="footnote-size"  clickable>2 Nephi 14:5.</Tooltip>
          
          This “smoke by day and the shining of a flaming
          fire by night” resting down upon Zion in Jackson County signifies the
          glory of the Lord as Joseph claimed he witnessed in the First Vision,
          “whose brightness and glory defy all description … above the
          brightness of the sun.”
        </p>
      </div>

      <div className="paragraph">
        <p>
          This glory is a shaft of heavenly light shining down upon the holy
          city, edifying and illuminating the inhabitants with light and truth.
          The holy city will be visited by innumerable angels and the Church of
          the Firstborn, which are exalted beings who have gained entrance into
          the highest degree of the Celestial kingdom. And, the glory of Zion,
          the brilliant light, will emanate not just from the city, but from the
          residents, kings and priests, queens and priestesses.
        </p>
      </div>

      <h2 className="headlinePadding" >Zion in the Pearl of Great Price</h2>

      <div className="paragraph">
        <p>
          Joseph Smith began revising the Bible after the church was founded. In
          December 1830 he dictated an account of another holy city, a New
          Jerusalem, known as the City of Enoch. The idea of building a holy
          city of Zion was a near obsession for Joseph. Five verses in the Old
          Testament mentions Enoch. “And Jared lived an hundred sixty and two
          years, and he begat Enoch … And Enoch lived sixty and five years, and
          begat Methuselah: And Enoch walked with God after he begat Methuselah
          three hundred years, and begat sons and daughters: And all the days of
          Enoch were three hundred sixty and five years: And Enoch walked with
          God: and he was not; for God took him.”
          <btn id="clickable24" className="footnote">24  </btn>
          <Tooltip anchorSelect="#clickable24" className="footnote-size"  clickable>Genesis 5:18-24.</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          Joseph produced 88 verses about Enoch, which are included in the Pearl
          of Great Price. In Joseph’s retelling,
          <btn id="clickable25" className="footnote">25  </btn>
          <Tooltip anchorSelect="#clickable25" className="footnote-size"  clickable>Moses 6-7.</Tooltip>
          
           “Enoch journeyed in the land …
          and as he journeyed, the Spirit of God descended out of heaven, and
          abode upon him.” Enoch hears a voice from heaven telling the people to
          repent. God is angry because the peoples’ hearts have waxed hard. “…
          ever since the day that I created them, have they gone astray, and
          have denied me, and have sought their own counsels in the dark.” Enoch
          begins preaching repentance and “no man laid hands on him; for fear
          came on all them that heard him; for he walked with God.” “And as
          Enoch spake forth the words of God, the people trembled and could not
          stand in his presence.”
        </p>
      </div>

      <div className="paragraph">
        <p>
          Enoch asks the people to be baptized in the name of Christ. “… all
          men, everywhere, must repent, or they can in nowise inherit the
          kingdom of God, for no unclean thing can dwell there.” Enoch beheld
          visions. “And from that time forth Enoch began to prophesy … as I
          stood upon the mount, I beheld the heavens open, and I
        </p>
      </div>

      <div className="paragraph">
        <p>
          Enoch had great faith. “And so great was the faith of Enoch that he
          led the people of God, and their enemies came to battle against them;
          and he spake the word of the Lord, and the earth trembled, and the
          mountains fled, even according to his command … and all nations feared
          greatly, so powerful was the word of Enoch, and so great was the power
          of the language which God had given him.” Eventually, Enoch and his
          people became so righteous that, “… the Lord came and dwelt with his
          people, and they dwelt in righteousness … And the Lord called his
          people Zion, because they were of one heart and one mind, and dwelt in
          righteousness; and there was no poor among them.” Enoch builds a city
          “called the City of Holiness, even Zion … [and] in process of time,
          [Zion] was taken up into heaven.”
        </p>
      </div>

      <div className="paragraph">
        <p>
          Next in the narrative, Enoch is shown historical events, including the
          death and resurrection of Christ. “And he heard a loud voice; and the
          heavens were veiled; and all the creations of God mourned; and the
          earth groaned; and the rocks were rent; and the saints arose and were
          crowned at the right hand of the Son of Man, with crowns of glory.”
          After watching Christ ascend to the Father, Enoch asks, “… wilt not
          come again on the earth.” Enoch is answered, “… I will come in the
          last days, in the days of wickedness and vengeance … and the heavens
          shall shake, and also the earth; and great tribulations shall be among
          the children of men.”
        </p>
      </div>

      <div className="paragraph">
        <p>
          The Lord continues, “And righteousness will I send down out of heaven;
          and truth will I send forth out of the earth, to bear testimony of
          mine Only Begotten … to sweep the earth as with a flood, to gather out
          mine elect from the four quarters of the earth, unto a place which I
          shall prepare, an Holy City (Jackson County), that my people may gird
          up their loins, and be looking forth for the time of my coming; for
          there shall be my tabernacle, and it shall be called Zion, a New
          Jerusalem.”
        </p>
      </div>

      <div className="paragraph">
        <p>
          Next the Lord tells Enoch that his holy city, which had fled the
          earth, in the last days will return. “… thy city [will] meet them
          there, and we will receive them into our bosom, and they shall see us;
          and we will fall upon their necks, and they shall fall upon our necks,
          and we will kiss each other; And there shall be mine abode, and it
          shall be Zion … and for the space of a thousand years the earth shall
          rest.” As in the Old Testament timeline, the events of Enoch’s life
          predate those of Noah. 
          <btn id="clickable26" className="footnote">26  </btn>
          <Tooltip anchorSelect="#clickable26" className="footnote-size"  clickable>According to George Laub journal, Joseph stated “The place or country where Noah’s ark was built was designated in my hearing by the Prophet Joseph Smith as being in or near South Carolina.” The Young Woman’s Journal, II, (December 1890), pp. 124-125; Hyrum L. Andrus and Helen Mae Andrus, They Knew the Prophet, 65.</Tooltip>
          
          These events, as described by Joseph Smith,
          occurred in the center of the United States. Therefore, the site of
          the latter-day Zion, the New Jerusalem, is believed to be the site of
          Enoch’s holy city of Zion.
        </p>
      </div>

      <h2 className="headlinePadding" >Becoming Kings and Priests, Queens and Priestesses in Zion</h2>

      <div className="paragraph">
        <p>
          According to Joseph, Zion must be established upon the Temple. This
          includes an order of kings and priests, queens and priestesses, who
          have built their lives on God’s laws of obedience, sacrifice, the
          Gospel, chastity, and consecration. Those who dwell in Zion are the
          truly righteous who have been sanctified from sin and the moral
          uncleanliness of this world. Through temple covenants and righteous
          living, they have been spiritually transformed. They will live worthy
          to receive their second anointings and to be sealed to the highest
          degree of exaltation in the celestial kingdom. They will receive the
          fullness of the priesthood, and have their callings and elections made
          sure.
        </p>
      </div>

      <div className="paragraph">
        <p>
          To Joseph, specific priesthood laws governed different celestial,
          terrestrial, and telestial glories. Priesthood was hierarchically
          arranged into “higher” and “lesser” orders. These hierarchies were
          governed by light and priesthood keys. The greater a person’s
          righteousness, the greater the priesthood hierarchy they achieve.
          Priesthood power was analogous to the positive forces of light. Think
          of the light of the sun and how it creates and sustain life. Joseph
          believed that through righteousness, and the priesthood, a man could
          sanctify himself to the point he could become commensurate with the
          Most High Gods in eternity.
        </p>
      </div>

      <div className="paragraph">
        <p>
          For Joseph, priesthood was an ancient sacred power that originated
          with the first God of all Gods. This is the power that Michael/Adam
          possessed and brought to this earth and into the Garden of Eden.
          According to Mormon scripture, priesthood power flowed from Adam
          through his lineage, eventually down to Abraham and then to Moses.
          <btn id="clickable27" className="footnote">27  </btn>
          <Tooltip anchorSelect="#clickable27" className="footnote-size"  clickable>D&C 84:6–18.</Tooltip>


          Joseph desired to confer this priesthood power on the early Mormons.
        </p>
      </div>

      <div className="paragraph">
        <p>
          For Joseph, priesthood is a power harnessed by the endless
          hierarchical chain of Gods to spread their dominion, creating and
          populating worlds. Through the priesthood, the Gods move from higher
          and higher spheres of righteousness and eternal mastery. The early
          Mormons were taught that through sealing ordinances, they could do the
          same.
        </p>
      </div>

      <div className="paragraph">
        <p>
          Joseph’s aim for his followers was to sanctify their lives and become
          filled with the tangible light of God’s glory. In a real, physical
          sense, a Mormon was to fill themselves with principles of heavenly
          light and truth. “That which is of God is light; and he that receiveth
          light, and continueth in God, receiveth more light; and that light
          groweth brighter and brighter until the perfect day.” 
          <btn id="clickable28" className="footnote">28  </btn>
          <Tooltip anchorSelect="#clickable28" className="footnote-size"  clickable>D&C 50:24.</Tooltip>
          
          Joseph
          explained, “And the redemption of the soul is through him that
          quickeneth all things … Therefore, it (the soul) must needs be
          sanctified from all unrighteousness, that it may be prepared for the
          celestial glory; For after it hath filled the measure of its creation,
          it shall be crowned with glory … That bodies who are of the celestial
          kingdom may possess it forever and ever; for, for this intent was it
          made and created, and for this intent are they sanctified.”
          <btn id="clickable29" className="footnote">29  </btn>
          <Tooltip anchorSelect="#clickable29" className="footnote-size"  clickable>D&C 88:17-20.</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          At a meeting of the Relief Society, Joseph promised that women could
          eventually “come in possession of the privileges, blessings, and gifts
          of the Priesthood” and would be given “the keys of the kingdom” if
          they were virtuous and diligent. 
          <btn id="clickable30" className="footnote">30  </btn>
          <Tooltip anchorSelect="#clickable30" className="footnote-size"  clickable>See “Journal, December 1841 – December 1842,” 94; “Nauvoo Relief Society Minute Book,” The Joseph Smith Papers, 38.</Tooltip>
          
          In an 1894 patriarchal blessing of
          Zina D. H. Young she was promised that she would “in connection with
          thy companion wear a Celestial crown in the highest order thereof and
          preside over thy posterity and the adopted ones throughout eternity.”
        </p>
        <btn id="clickable31" className="footnote">31  </btn>
        <Tooltip anchorSelect="#clickable31" className="footnote-size"  clickable>Daniel Tyler, Patriarchal Blessing to Zina Diantha Huntington Smith Young, September 16, 1894.</Tooltip>
      </div>

      <div className="paragraph">
        <p>
          In his King Follett sermon, Joseph explained that to be “joint heirs
          with Christ” 
          <btn id="clickable32" className="footnote">32  </btn>
          <Tooltip anchorSelect="#clickable32" className="footnote-size"  clickable>Romans 8:17.</Tooltip>
          
          meant “to inherit the same glory power & exaltation” and
          to “ascend [to] a throne as those who have gone before.” Speaking for
          Christ, Joseph stated, “… when I get my kingdom worked out, I will
          present to the father & it will exalt his glory” so that “he will take
          a higher exaltation & I will take his place and am also exalted.”
          Thus, the Father “obtains kingdom rolling upon kingdom so that Jesus
          treads in his tracks as he had gone before.”
          <btn id="clickable33" className="footnote">33  </btn>
          <Tooltip anchorSelect="#clickable33" className="footnote-size"  clickable>Ehat and Cook, Words of Joseph Smith, 341, 345, 350, 358.</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          George Laub was present for the King Follet discourse. He understood
          Joseph to say, “We are to go from glory to glory & as one is raised,
          the next may be raised to his place or sphere and so take their
          exaltation through a regular channel. And when we get to where Jesus
          is he will be as far ahead of us in exaltation as when we started.”
          <btn id="clickable34" className="footnote">34  </btn>
          <Tooltip anchorSelect="#clickable34" className="footnote-size"  clickable>Eugene England, “George Laub’s Nauvoo Journal,” 173; see also Ehat and Cook, Words of Joseph Smith, 362</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          In short, the residents of Zion will work to achieve godhood. As they
          increased in righteousness, they receive endowments of glory and power
          of gods as they climb the ladder of exaltation. Joseph taught, “When
          you climb a ladder, you must begin at the bottom and go on until you
          learn the last principle; it will be a great while before you have
          learned the last … It is a great thing to learn salvation beyond the
          grave.” 
          <btn id="clickable35" className="footnote">35  </btn>
          <Tooltip anchorSelect="#clickable35" className="footnote-size"  clickable>Conference Minutes, 614. Compare to the James Burgess account in Ehat and Cook, The Words of Joseph Smith, 350.</Tooltip>
          
          Joseph was telling his followers that they could ascend
          Jacob’s Ladder and became gods. “Then shall they be gods, because they
          have no end; therefore, shall they be from everlasting to everlasting,
          because they continue; then shall they be above all, because all
          things are subject unto them. Then shall they be gods, because they
          have all power, and the angels are subject unto them.”
          <btn id="clickable36" className="footnote">36  </btn>
          <Tooltip anchorSelect="#clickable36" className="footnote-size"  clickable>“Conference Minutes,” 614. Compare to the James Burgess account in Ehat and Cook, The Words of Joseph Smith, 350</Tooltip>
        </p>
      </div>

      <div className="paragraph">
        <p>
          Joseph told his followers, “You have got to learn how to be Gods
          yourselves; to be kings and priests to God, the same as all Gods have
          done; by going from a small degree to another, from grace to grace,
          from exaltation to exaltation, until you are able to sit in glory as
          doeth those who sit enthroned in everlasting power.” Therefore,
          through a process of sanctification and by the power of the
          priesthood, the early Mormons believed that they could be transformed
          into gods. They would one day take up the matter of the universe, and
          by applying the laws of the Gods, expand the cosmos to create and
          people worlds.
        </p>
      </div>

      <div className="paragraph">
        <p>
          For Joseph, this all begins in Zion and establishing the Kingdom of
          God on the earth. This is exciting stuff the Mormon church of today
          will not discuss. No wonder all but a few Mormons are asleep in
          Sacrament meeting.
        </p>
      </div>

      </div>

      <div className="chapterFooter">
        <Link to="/Chap3">Previous</Link>
        <Link to="/">Home</Link>
        <Link to="/Chap5">Next</Link>
      </div>
      
    </div>
  );
};

export default Chap4;
